import {
  useHiddenSafeMeasureElement,
  useIsElementHidden,
} from '@/components/CardTable/hooks/useHiddenSafeMeasurement';
import { getScrollableAncestor } from '@/utils/dom/getScrollableAncestor';
import { useVirtualizer, type VirtualItem } from '@tanstack/react-virtual';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from 'react';

export type UseVirtualTableRowsArgs = {
  count: number;
  /** First-paint row height guess (px); corrected per-row via `measureElement`. */
  rowHeight: number;
  overscan?: number;
  getItemKey: (index: number) => string | number;
};

export type UseVirtualTableRowsResult = {
  /** Attach to the element that sits where the table body starts. */
  wrapperRef: RefObject<HTMLDivElement>;
  virtualItems: VirtualItem[];
  /** Spacer height (px) to reserve above the rendered window. */
  before: number;
  /** Spacer height (px) to reserve below the rendered window. */
  after: number;
  /** The same scroll ancestor the virtualizer windows against (the drawer). */
  getScrollElement: () => Element | null;
  /**
   * Attach to each rendered row's root element (alongside `data-index`) so the
   * virtualizer corrects its per-row offset against the real rendered height
   * instead of trusting `rowHeight` forever.
   */
  measureElement: (node: Element | null) => void;
};

// `useLayoutEffect` measures before paint (no misaligned-row flash) but warns
// under SSR; fall back to `useEffect` on the server.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Windows a row list against the nearest scrollable ancestor (e.g. the drawer
 * body) instead of an internal scroll viewport, so the table keeps a single
 * scrollbar and grows with the surrounding page — same idea as the card
 * layout's `windowScroll`.
 *
 * `rowHeight` is only a first-paint guess: nothing enforces a uniform height
 * against the design system's own padding/line-height/border, and a cell that
 * wraps makes its row taller. The consumer feeds each rendered row's element
 * back through `measureElement`, so the virtualizer corrects its offsets to
 * the real height — the same mechanism `CardTable/VirtualList` uses — instead
 * of the `before`/`after` spacers drifting out of sync with the browser's real
 * scroll geometry (the scrollbar-jitter bug).
 *
 * Measurement is routed through `useHiddenSafeMeasureElement`, and both
 * `scrollMargin` measurements bail out while the wrapper is hidden. A
 * collapsible `SectionHeading` toggles `display: none` *without* unmounting,
 * and a hidden element has no layout boxes, so every measurement reads 0 —
 * caching those zeroes collapses the total size and spins the
 * measure/resize/re-render loop that locked the main thread and crashed the
 * drawer. See `useHiddenSafeMeasurement` for the full mechanism.
 */
export const useVirtualTableRows = ({
  count,
  rowHeight,
  overscan = 12,
  getItemKey,
}: UseVirtualTableRowsArgs): UseVirtualTableRowsResult => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const scrollAncestorRef = useRef<Element | null>(null);

  const getScrollElement = useCallback((): Element | null => {
    if (!scrollAncestorRef.current) {
      scrollAncestorRef.current = getScrollableAncestor(wrapperRef.current);
    }
    return scrollAncestorRef.current ?? wrapperRef.current;
  }, []);

  // Offset of the table's top from the top of the scroll element's content.
  // Invariant to scroll position; only changes when content above the table
  // (or the layout) resizes, so it is recomputed only on those.
  const [scrollMargin, setScrollMargin] = useState(0);

  const isWrapperHidden = useIsElementHidden(wrapperRef);

  const measureScrollMargin = useCallback(() => {
    const wrapper = wrapperRef.current;
    const scrollEl = getScrollElement();
    if (!wrapper || !scrollEl || scrollEl === wrapper) return;
    // A hidden wrapper has no layout boxes, so every rect reads 0 — writing
    // that to `scrollMargin` would move every row's offset to the top of the
    // drawer and only be corrected on the next resize.
    if (isWrapperHidden()) return;
    const next =
      wrapper.getBoundingClientRect().top -
      scrollEl.getBoundingClientRect().top +
      scrollEl.scrollTop;
    // Guard against measurement jitter causing render churn.
    setScrollMargin((prev) => (Math.abs(prev - next) > 1 ? next : prev));
  }, [getScrollElement, isWrapperHidden]);

  useIsomorphicLayoutEffect(() => {
    measureScrollMargin();

    const scrollEl = getScrollElement();
    const observer = new ResizeObserver(measureScrollMargin);
    if (wrapperRef.current) observer.observe(wrapperRef.current);
    if (scrollEl && scrollEl !== wrapperRef.current) {
      observer.observe(scrollEl);
      // The table's offset shifts whenever a sibling *above* it grows or
      // shrinks; those siblings are children of the scroll container and
      // resize without touching the wrapper or the scroll element's own
      // border box, so observing them directly keeps `scrollMargin` correct.
      Array.from(scrollEl.children).forEach((child) => observer.observe(child));
    }
    window.addEventListener('resize', measureScrollMargin);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measureScrollMargin);
    };
  }, [getScrollElement, count, measureScrollMargin]);

  // Ignores zero-height readings taken while the section is collapsed; see
  // the note on this hook's docblock.
  const measureRowElement = useHiddenSafeMeasureElement(rowHeight);

  const virtualizer = useVirtualizer({
    count,
    getScrollElement,
    estimateSize: useCallback(() => rowHeight, [rowHeight]),
    overscan,
    getItemKey,
    scrollMargin,
    measureElement: measureRowElement,
  });

  // Safety net for the frame this table appears in (e.g. a layout switch): the
  // virtualizer only recomputes its visible range on a scroll/resize event, so
  // if nothing fires it can hold a stale range and render a blank window until
  // the user resizes the drawer. On the next frame, re-measure and dispatch a
  // synthetic scroll so the virtualizer re-reads the current offset and range.
  useIsomorphicLayoutEffect(() => {
    const raf = requestAnimationFrame(() => {
      // Nothing to re-range while collapsed, and the synthetic scroll would
      // only make the virtualizer re-read a zeroed geometry.
      if (isWrapperHidden()) return;
      measureScrollMargin();
      const scrollEl = getScrollElement();
      if (scrollEl && scrollEl !== wrapperRef.current) {
        scrollEl.dispatchEvent(new Event('scroll'));
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [getScrollElement, measureScrollMargin, isWrapperHidden, count]);

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  const firstItem = virtualItems[0];
  const lastItem = virtualItems[virtualItems.length - 1];

  const before = firstItem ? firstItem.start - scrollMargin : 0;
  const after = lastItem ? totalSize - (lastItem.end - scrollMargin) : 0;

  return {
    wrapperRef,
    virtualItems,
    before,
    after,
    getScrollElement,
    measureElement: virtualizer.measureElement,
  };
};
